<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts';
import { useCommandBar } from './composables/useCommandBar';
import { useTheme } from './composables/useTheme';
import { useHelp } from './composables/useHelp';
import CommandBar from './components/CommandBar.vue';
import ToastContainer from './components/common/ToastContainer.vue';
import KeyboardShortcutsModal from './components/common/KeyboardShortcutsModal.vue';
import HelpPanel from './components/help/HelpPanel.vue';
import OfflineBanner from './components/common/OfflineBanner.vue';
import DialogHost from './components/common/DialogHost.vue';

const router = useRouter();
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const { registerShortcut } = useKeyboardShortcuts();
const { open: openCommandBar } = useCommandBar();
const { toggleTheme } = useTheme();
const { openContextualHelp } = useHelp();

// Register global keyboard shortcuts
function registerGlobalShortcuts() {
  // Navigation shortcuts
  registerShortcut('g+h', () => router.push('/'), {
    description: 'Go to Home/Dashboard',
    category: 'Navigation',
  });

  registerShortcut('g+j', () => router.push('/jobs'), {
    description: 'Go to Jobs',
    category: 'Navigation',
  });

  registerShortcut('g+s', () => router.push('/suppliers'), {
    description: 'Go to Suppliers',
    category: 'Navigation',
  });

  registerShortcut('g+c', () => router.push('/contacts'), {
    description: 'Go to Contacts',
    category: 'Navigation',
  });

  registerShortcut('g+d', () => router.push('/documents'), {
    description: 'Go to Documents',
    category: 'Navigation',
  });

  registerShortcut('g+a', () => router.push('/accounting'), {
    description: 'Go to Accounting',
    category: 'Navigation',
  });

  registerShortcut('g+p', () => router.push('/catalogue'), {
    description: 'Go to Price Catalogue',
    category: 'Navigation',
  });

  // General shortcuts (Ctrl+K handled by CommandBar)
  registerShortcut('ctrl+k', openCommandBar, {
    description: 'Open Command Palette',
    category: 'General',
  });

  registerShortcut('esc', () => {
    // Escape is handled by modals, just document it
  }, {
    description: 'Close modal / Cancel',
    category: 'General',
  });

  // Theme toggle shortcut
  registerShortcut('ctrl+shift+l', toggleTheme, {
    description: 'Toggle dark/light mode',
    category: 'General',
  });

  // Help shortcuts
  registerShortcut('f1', openContextualHelp, {
    description: 'Open Help',
    category: 'General',
  });
}

onMounted(async () => {
  // Try to restore session from localStorage
  await authStore.restoreSession();

  // Register keyboard shortcuts when authenticated
  if (isAuthenticated.value) {
    registerGlobalShortcuts();
  }
});
</script>

<template>
  <div id="app-root">
    <OfflineBanner />
    <CommandBar v-if="isAuthenticated" />
    <RouterView />
    <ToastContainer />
    <DialogHost />
    <KeyboardShortcutsModal />
    <HelpPanel v-if="isAuthenticated" />
  </div>
</template>

<style>
:root {
  --primary-color: #3b82f6;
  --primary-hover: #2563eb;
  --sidebar-width: 240px;
  --header-height: 56px;
}

#app,
#app-root {
  height: 100%;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--dbx-bg-primary);
  color: var(--dbx-text-primary);
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Smooth transition for theme changes */
html {
  transition: background-color 0.2s ease;
}

html[data-bs-theme="dark"] {
  background-color: #0f172a;
  color-scheme: dark;
}

html[data-bs-theme="light"] {
  background-color: #ffffff;
  color-scheme: light;
}
</style>
